import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import JudgeHeader from '../../components/JudgeHeader';
import AudioPlayer from '../../components/AudioPlayer';
import Tooltip from '../../components/Tooltip';
import './judge.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MAX_COMMENTS = 2000;

function EvalBadge({ status }) {
  const map = {
    pending:   'badge badge--pending',
    draft:     'badge badge--draft',
    submitted: 'badge badge--submitted',
  };
  return (
    <span className={map[status] ?? 'badge badge--pending'}>
      {status ?? 'pending'}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day:   'numeric',
    year:  'numeric',
  });
}

// ---------------------------------------------------------------------------
// Score radio group
// ---------------------------------------------------------------------------

function ScoreRadios({ criterionId, max, value, onChange, readOnly }) {
  return (
    <div className="j-score-radios" role="radiogroup">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <label
          key={n}
          className={
            'j-score-radio-label' +
            (value === n     ? ' is-selected' : '') +
            (readOnly        ? ' is-readonly'  : '')
          }
          aria-label={`Score ${n}`}
        >
          <input
            type="radio"
            name={`score-${criterionId}`}
            value={n}
            checked={value === n}
            onChange={() => !readOnly && onChange(criterionId, n)}
            disabled={readOnly}
          />
          <span>{n}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function JudgeEvaluate() {
  const { songId } = useParams();

  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [detail, setDetail]       = useState(null);
  // detail shape: { assignment_id, assigned_at, max_score_per_criterion,
  //                 song, evaluation, scoring_criteria }

  // Form state
  const [scores, setScores]             = useState({});   // { criterion_id: score }
  const [comments, setComments]         = useState('');
  const [placementCategoryId, setPlacementCategoryId] = useState(null);
  const [placementCategories, setPlacementCategories] = useState([]);

  // Submission state
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [commentsError, setCommentsError] = useState('');

  // Ready-for-submission state
  const [ready, setReady]         = useState(false);
  const [togglingReady, setTogglingReady] = useState(false);

  // Submit confirmation modal
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Unsaved-work tracking. Judges were losing scores by navigating away without
  // pressing Save Draft, so leaving the page saves for them. baselineRef holds
  // the last state known to be on the server.
  const navigate    = useNavigate();
  const baselineRef = useRef(null);
  const [leaving, setLeaving] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);

  // ── Load song detail + placement categories ──────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get(`/api/judges/songs/${songId}`),
      api.get('/api/judges/placement-categories'),
    ])
      .then(([songRes, catRes]) => {
        setDetail(songRes.data);
        setReady(songRes.data.ready_for_submission ?? false);
        setPlacementCategories(catRes.data.categories ?? []);
        if (songRes.data.evaluation) {
          const init = {};
          (songRes.data.evaluation.scores ?? []).forEach((s) => {
            init[s.criterion_id] = s.score;
          });
          setScores(init);
          setComments(songRes.data.evaluation.comments ?? '');
          setPlacementCategoryId(songRes.data.evaluation.placement_category_id ?? null);
          baselineRef.current = JSON.stringify({
            scores: init,
            comments: songRes.data.evaluation.comments ?? '',
            placementCategoryId: songRes.data.evaluation.placement_category_id ?? null,
          });
        } else {
          baselineRef.current = JSON.stringify({ scores: {}, comments: '', placementCategoryId: null });
        }
      })
      .catch((err) => {
        setError(err.response?.data?.error ?? 'Failed to load song details.');
      })
      .finally(() => setLoading(false));
  }, [songId]);

  const handleScoreChange = useCallback((criterionId, value) => {
    setScores((prev) => ({ ...prev, [criterionId]: value }));
    setSaveSuccess('');
    setSaveError('');
  }, []);

  // ── Derived: category flags ─────────────────────────────────────────────
  const categoryName   = detail?.song?.category_name?.toLowerCase() ?? '';
  const isLyric        = categoryName === 'lyric';
  const isInstrumental = categoryName === 'instrumental';
  const isFoomLive     = categoryName === 'foom live';

  // Criteria visible in the form (filtered by category)
  const visibleCriteria = isLyric
    ? []
    : isInstrumental
    ? (detail?.scoring_criteria ?? []).filter((c) => c.name?.toLowerCase() !== 'lyric')
    : (detail?.scoring_criteria ?? []);

  // ── Save (draft or final) ───────────────────────────────────────────────
  // opts.silent — an autosave: no green banner, just the quiet timestamp, so
  // the page does not flash a success message every few seconds while typing.
  async function save(submit, opts = {}) {
    setSaveError('');
    if (!opts.silent) setSaveSuccess('');
    setCommentsError('');

    if (submit && !comments.trim()) {
      setCommentsError(
        isLyric
          ? 'Feedback is required before submitting.'
          : 'Comments are required before submitting.'
      );
      return;
    }

    setSaving(true);

    const { assignment_id, evaluation } = detail;

    // Build scores array from visible criteria only
    const scoresArray = visibleCriteria
      .filter((c) => scores[c.id] != null)
      .map((c) => ({ criterion_id: c.id, score: scores[c.id] }));

    const body = { scores: scoresArray, comments, submit, placement_category_id: placementCategoryId };

    try {
      const method = evaluation ? 'put' : 'post';
      const res = await api[method](
        `/api/judges/evaluate/${assignment_id}`,
        body
      );
      setDetail((prev) => ({ ...prev, evaluation: res.data.evaluation }));
      baselineRef.current = JSON.stringify({ scores, comments, placementCategoryId });
      if (opts.silent) {
        setAutoSavedAt(new Date());
      } else {
        setAutoSavedAt(null);
        setSaveSuccess(
          submit
            ? 'Evaluation submitted! Your scores are locked.'
            : 'Draft saved.'
        );
      }
      return true;
    } catch (err) {
      const msg =
        err.response?.data?.error ??
        (err.response?.data?.errors
          ? err.response.data.errors.join(' ')
          : null) ??
        'Failed to save. Please try again.';
      setSaveError(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }

  // ── Unsaved work ────────────────────────────────────────────────────────
  // A submitted evaluation is read-only, and FOOM Live entries have no form,
  // so neither can be dirty.
  const isDirty =
    detail?.evaluation?.is_submitted !== true &&   // isReadOnly is declared below
    !isFoomLive &&
    baselineRef.current !== null &&
    baselineRef.current !== JSON.stringify({ scores, comments, placementCategoryId });

  // Closing the tab or hitting refresh cannot be intercepted to save, so warn.
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  // Autosave a couple of seconds after the judge stops changing things. The
  // timer restarts on every edit, so typing a long comment saves once at the
  // end rather than on every keystroke. Skipped while another save is running
  // so the two cannot race.
  useEffect(() => {
    if (!isDirty || saving || leaving) return undefined;
    const t = setTimeout(() => { save(false, { silent: true }); }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, saving, leaving, scores, comments, placementCategoryId]);

  // Leaving via the in-page link saves the draft first. If the save fails the
  // judge stays put with the error visible rather than losing the work.
  async function handleBackToDashboard(e) {
    if (!isDirty) return;              // let the Link navigate normally
    e.preventDefault();
    setLeaving(true);
    const ok = await save(false);
    setLeaving(false);
    if (ok) navigate('/judge/dashboard');
  }

  // ── Toggle ready-for-submission ─────────────────────────────────────────
  async function toggleReady() {
    setTogglingReady(true);
    try {
      const res = await api.post(`/api/judges/assignments/${detail.assignment_id}/ready`);
      setReady(res.data.ready_for_submission);
    } catch {
      // silently ignore — state unchanged
    } finally {
      setTogglingReady(false);
    }
  }

  // ── Derived values ──────────────────────────────────────────────────────
  const isReadOnly = detail?.evaluation?.is_submitted === true;
  const evalStatus = !detail?.evaluation
    ? 'pending'
    : detail.evaluation.is_submitted
    ? 'submitted'
    : 'draft';

  const maxScore = detail?.max_score_per_criterion ?? 5;

  // All visible criteria must be scored for submission
  const allScored = visibleCriteria.every((c) => scores[c.id] != null);

  // ── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="c-page">
        <JudgeHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" aria-label="Loading…" />
            <p>Loading song details…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="c-page">
        <JudgeHeader />
        <main className="c-main">
          <Link to="/judge/dashboard" className="c-back-btn">← Back to Dashboard</Link>
          <div className="c-alert" role="alert">{error}</div>
        </main>
      </div>
    );
  }

  const { song, evaluation } = detail;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="c-page">
      <JudgeHeader />

      <main className="c-main">
        <Link to="/judge/dashboard" className="c-back-btn" onClick={handleBackToDashboard}>
          {leaving ? 'Saving…' : '← Back to Dashboard'}
        </Link>

        {/* ── Song detail card ── */}
        <div className="j-song-card">
          <div className="j-song-card__meta" style={{ marginBottom: '0.4rem' }}>
            <EvalBadge status={evalStatus} />
            {evaluation?.submitted_at && (
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                Submitted {formatDate(evaluation.submitted_at)}
              </span>
            )}
          </div>

          <h1 className="j-song-card__title">{song.title}</h1>

          <div className="j-song-card__meta">
            <span className="badge badge--submitted">{song.category_name}</span>
          </div>

          {song.lyrics_notes && (
            <div className="j-song-card__notes">
              <strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Performer Notes
              </strong>
              <p style={{ margin: '0.3rem 0 0' }}>{song.lyrics_notes}</p>
            </div>
          )}

          {/* Lyrics shown in song card for non-Lyric categories (if present) */}
          {song.lyrics_text && !isLyric && (
            <div className="j-song-card__lyrics">
              <strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Lyrics
              </strong>
              <div className="j-lyrics-text">
                {song.lyrics_text.split(/\n\n+/).map((para, idx) => (
                  <p key={idx} className="j-lyrics-para">
                    {para.split('\n').map((line, li) => (
                      <span key={li}>
                        {li > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </p>
                ))}
              </div>
            </div>
          )}

          {song.audio_url && (
            <div className="j-song-card__audio">
              <AudioPlayer src={song.audio_url} />
              <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <button
                  type="button"
                  className="c-btn c-btn--outline c-btn--sm"
                  disabled={downloading}
                  onClick={async () => {
                    setDownloading(true);
                    setDownloadError('');
                    try {
                      const resp = await api.get(
                        `/api/judges/assignments/${detail.assignment_id}/download-audio`,
                        { responseType: 'blob' }
                      );
                      const url = URL.createObjectURL(new Blob([resp.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      // Use filename from Content-Disposition if available, else fallback
                      const cd = resp.headers['content-disposition'] || '';
                      const match = cd.match(/filename="?([^"]+)"?/);
                      a.download = match ? match[1] : `${song.title || 'song'}.mp3`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch {
                      setDownloadError('Download failed. Please try again.');
                    } finally {
                      setDownloading(false);
                    }
                  }}
                >
                  {downloading ? '⏳ Downloading…' : '⬇ Download Song'}
                </button>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                  If the song won't play, download it and play it from your computer.
                </p>
                {downloadError && (
                  <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: 0 }}>{downloadError}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── FOOM Live: no online judging ── */}
        {isFoomLive && (
          <div className="c-alert c-alert--info" role="status" style={{ marginTop: '1.5rem' }}>
            FOOM Live songs are judged in-person at the heat. No online judging required.
          </div>
        )}

        {/* ── All other categories: evaluation form ── */}
        {!isFoomLive && (
          <>
            {/* ── Read-only submitted notice ── */}
            {isReadOnly && (
              <div className="c-alert c-alert--success" role="status">
                <strong>Evaluation submitted.</strong> Your scores are finalised and
                cannot be modified.
              </div>
            )}

            {/* ── Feedback banners ── */}
            {saveError && (
              <div className="c-alert" role="alert">{saveError}</div>
            )}
            {saveSuccess && (
              <div className="c-alert c-alert--success" role="status">
                {saveSuccess}
              </div>
            )}
            {/* Quiet autosave status — deliberately not an alert banner, so it
                does not shout every few seconds while a judge is working. */}
            {!saveSuccess && (isDirty || autoSavedAt) && (
              <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }} role="status">
                {saving
                  ? 'Saving…'
                  : isDirty
                    ? 'Unsaved changes — these save automatically.'
                    : `Draft saved automatically at ${autoSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}.`}
              </p>
            )}

            <div className="j-eval-form">

              {/* ── Lyric category: prominent lyrics display + feedback only ── */}
              {isLyric ? (
                <>
                  {/* Lyrics read-only display */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h2 className="j-eval-form__title">Lyrics</h2>
                    {song.lyrics_text ? (
                      <div
                        className="j-lyrics-text"
                        style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          padding: '1rem 1.25rem',
                          fontSize: '0.95rem',
                          lineHeight: 1.7,
                        }}
                      >
                        {song.lyrics_text.split(/\n\n+/).map((para, idx) => (
                          <p key={idx} className="j-lyrics-para">
                            {para.split('\n').map((line, li) => (
                              <span key={li}>
                                {li > 0 && <br />}
                                {line}
                              </span>
                            ))}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.9rem' }}>
                        No lyrics text was submitted with this song.
                      </p>
                    )}
                  </div>

                  {/* Feedback textarea (maps to evaluation.comments) */}
                  <div className="j-comments">
                    <label className="c-label" htmlFor="eval-comments">
                      Feedback <span className="c-label__required">*</span>
                      <Tooltip tooltipKey="judge_comments" />
                    </label>
                    {isReadOnly ? (
                      <div className="j-comments__readonly">
                        {evaluation?.comments || (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                            No feedback provided.
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        <textarea
                          id="eval-comments"
                          className="c-textarea"
                          rows={8}
                          maxLength={MAX_COMMENTS}
                          value={comments}
                          onChange={(e) => {
                            setComments(e.target.value);
                            setSaveSuccess('');
                            setSaveError('');
                            setCommentsError('');
                          }}
                          placeholder="Provide written feedback on the lyrics for the contestant…"
                        />
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: comments.length > MAX_COMMENTS * 0.9 ? '#d97706' : '#9ca3af',
                            textAlign: 'right',
                            marginTop: '0.25rem',
                          }}
                        >
                          {comments.length} / {MAX_COMMENTS}
                        </p>
                        {commentsError && (
                          <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            {commentsError}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* ── Standard scoring section (Instrumental / Unreleased Recording) ── */}
                  <h2 className="j-eval-form__title">
                    Score this Song
                    <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                      (1 = lowest, {maxScore} = highest)
                    </span>
                  </h2>

                  {visibleCriteria.length === 0 ? (
                    <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      No scoring criteria have been configured yet.
                    </p>
                  ) : (
                    visibleCriteria.map((c) => (
                      <div key={c.id} className="j-criterion">
                        <div className="j-criterion__header">
                          <div>
                            <p className="j-criterion__name">
                              {c.name}
                              {c.weight && c.weight !== 1 && (
                                <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.4rem' }}>
                                  ×{c.weight} weight
                                </span>
                              )}
                              <Tooltip tooltipKey="judge_scoring_criteria" />
                            </p>
                            {c.description && (
                              <p className="j-criterion__desc">{c.description}</p>
                            )}
                          </div>
                          <div className="j-criterion__score-area">
                            <ScoreRadios
                              criterionId={c.id}
                              max={maxScore}
                              value={scores[c.id] ?? null}
                              onChange={handleScoreChange}
                              readOnly={isReadOnly}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {/* ── Comments ── */}
                  <div className="j-comments">
                    <label className="c-label" htmlFor="eval-comments">
                      Comments <span className="c-label__required">*</span>
                      <Tooltip tooltipKey="judge_comments" />
                    </label>
                    {isReadOnly ? (
                      <div className="j-comments__readonly">
                        {evaluation?.comments || (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                            No comments provided.
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        <textarea
                          id="eval-comments"
                          className="c-textarea"
                          rows={5}
                          maxLength={MAX_COMMENTS}
                          value={comments}
                          onChange={(e) => {
                            setComments(e.target.value);
                            setSaveSuccess('');
                            setSaveError('');
                            setCommentsError('');
                          }}
                          placeholder="Provide feedback for the contestant or notes for other judges…"
                        />
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: comments.length > MAX_COMMENTS * 0.9 ? '#d97706' : '#9ca3af',
                            textAlign: 'right',
                            marginTop: '0.25rem',
                          }}
                        >
                          {comments.length} / {MAX_COMMENTS}
                        </p>
                        {commentsError && (
                          <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            {commentsError}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              {/* ── Placement Category ── */}
              {placementCategories.length > 0 && (
                <div className="j-comments" style={{ marginTop: '1.5rem' }}>
                  <p className="c-label" style={{ marginBottom: '0.5rem' }}>
                    Placement Category
                    <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.8rem', marginLeft: '0.4rem' }}>
                      (optional)
                    </span>
                  </p>
                  {isReadOnly ? (
                    <div className="j-comments__readonly">
                      {placementCategoryId
                        ? (placementCategories.find((c) => c.id === placementCategoryId)?.name ?? 'Unknown')
                        : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No designation set.</span>
                      }
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="placement-category"
                          value=""
                          checked={placementCategoryId === null}
                          onChange={() => setPlacementCategoryId(null)}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#374151' }}>None</span>
                      </label>
                      {placementCategories.map((cat) => (
                        <label key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="placement-category"
                            value={cat.id}
                            checked={placementCategoryId === cat.id}
                            onChange={() => setPlacementCategoryId(cat.id)}
                          />
                          <span style={{ fontSize: '0.9rem', color: '#374151' }}>{cat.name}</span>
                          {cat.description && (
                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>— {cat.description}</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Action buttons ── */}
              {!isReadOnly && (
                <>
                  <div className="j-actions">
                    <button
                      type="button"
                      className="c-btn c-btn--outline"
                      onClick={() => save(false)}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="c-spinner c-spinner--sm" aria-hidden="true" />
                          Saving…
                        </>
                      ) : (
                        'Save Draft'
                      )}
                    </button>

                    {/* ── Mark as Ready toggle ── */}
                    <button
                      type="button"
                      onClick={toggleReady}
                      disabled={togglingReady}
                      style={{
                        background:   ready ? '#22c55e' : 'transparent',
                        color:        ready ? '#fff'    : '#22c55e',
                        border:       '2px solid #22c55e',
                        borderRadius: '6px',
                        padding:      '0.45rem 1rem',
                        fontWeight:   600,
                        fontSize:     '0.9rem',
                        cursor:       togglingReady ? 'not-allowed' : 'pointer',
                        opacity:      togglingReady ? 0.7 : 1,
                        transition:   'background 0.15s, color 0.15s',
                      }}
                    >
                      {ready ? '✓ Ready for Submission' : 'Mark as Ready'}
                    </button>

                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        type="button"
                        className="c-submit-btn"
                        onClick={() => setShowSubmitModal(true)}
                        disabled={saving || !allScored || !comments.trim()}
                        title={
                          !allScored
                            ? 'Score every criterion before submitting'
                            : !comments.trim()
                            ? (isLyric ? 'Add feedback before submitting' : 'Add comments before submitting')
                            : undefined
                        }
                      >
                        Submit Final Evaluation
                      </button>
                      <Tooltip tooltipKey="judge_submit" />
                    </span>
                  </div>

                  {/* ── Ready note ── */}
                  {ready && (
                    <p style={{ fontSize: '0.8rem', color: '#16a34a', marginTop: '0.5rem', textAlign: 'center' }}>
                      Marked as ready — you can still make changes or submit your final evaluation
                    </p>
                  )}
                </>
              )}

              {!isReadOnly && (!allScored || !comments.trim()) && (
                <p style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.5rem', textAlign: 'center' }}>
                  {isLyric
                    ? 'Add feedback to enable final submission.'
                    : !allScored
                    ? `Score all ${visibleCriteria.length} criteria and add comments to enable final submission.`
                    : 'Add comments to enable final submission.'}
                </p>
              )}
            </div>
          </>
        )}

        <div style={{ marginTop: '32px' }}>
          <Link to="/judge/dashboard" className="c-back-btn" onClick={handleBackToDashboard}>
            {leaving ? 'Saving…' : '← Back to Dashboard'}
          </Link>
          {isDirty && !leaving && (
            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0.5rem 0 0' }}>
              You have unsaved changes — they will be saved as a draft when you go back.
            </p>
          )}
        </div>
      </main>

      {/* ── Submit confirmation modal ── */}
      {showSubmitModal && (
        <div
          style={{
            position:        'fixed',
            inset:           0,
            background:      'rgba(0,0,0,0.45)',
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            zIndex:          1000,
          }}
          onClick={() => setShowSubmitModal(false)}
        >
          <div
            style={{
              background:   '#fff',
              borderRadius: '10px',
              padding:      '2rem',
              maxWidth:     '420px',
              width:        '90%',
              boxShadow:    '0 8px 32px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.15rem', fontWeight: 700 }}>
              Submit Final Evaluation
            </h2>
            <p style={{ margin: '0 0 1.5rem', color: '#374151', lineHeight: 1.5 }}>
              Once submitted, your evaluation cannot be changed. Are you sure you want
              to submit your final scores and comments for this song?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="c-btn c-btn--outline"
                onClick={() => setShowSubmitModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="c-submit-btn"
                disabled={saving}
                onClick={() => {
                  setShowSubmitModal(false);
                  save(true);
                }}
              >
                {saving ? (
                  <>
                    <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                    Submitting…
                  </>
                ) : (
                  'Confirm Submit'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
